import {
  PropsWithChildren,
  createContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  getSessionIdFromPath,
  isAllowedProjectsOrigin,
  PLAYGROUND_AUTH_REQUEST,
  PLAYGROUND_AUTH_RESPONSE,
} from "../auth/playgroundAuth";
import { getIsEmbedded } from "../utilities/getIsEmbedded";

interface PlaygroundAuthContextType {
  token: string | null;
  isEmbedded: boolean;
  isAuthReady: boolean;
  canSave: boolean;
}

export const PlaygroundAuthContext = createContext<PlaygroundAuthContextType>({
  token: null,
  isEmbedded: false,
  isAuthReady: true,
  canSave: false,
});

export function PlaygroundAuthProvider({ children }: PropsWithChildren) {
  const isEmbedded = getIsEmbedded();
  const [token, setToken] = useState<string | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(!isEmbedded);

  useEffect(() => {
    if (!isEmbedded) return;

    const sessionId = getSessionIdFromPath(window.location.pathname);
    const parentOrigin = document.referrer
      ? new URL(document.referrer).origin
      : null;
    if (!sessionId || !parentOrigin || !isAllowedProjectsOrigin(parentOrigin)) {
      setIsAuthReady(true);
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (
        event.source !== window.parent ||
        !isAllowedProjectsOrigin(event.origin)
      )
        return;
      if (
        event.data?.type !== PLAYGROUND_AUTH_RESPONSE ||
        event.data?.sessionId !== sessionId
      )
        return;

      setToken(
        typeof event.data.token === "string" && event.data.token
          ? event.data.token
          : null,
      );
      setIsAuthReady(true);
    };

    window.addEventListener("message", handleMessage);
    window.parent.postMessage(
      { type: PLAYGROUND_AUTH_REQUEST, sessionId },
      parentOrigin,
    );

    return () => window.removeEventListener("message", handleMessage);
  }, [isEmbedded]);

  const value = useMemo(
    () => ({ token, isEmbedded, isAuthReady, canSave: Boolean(token) }),
    [token, isEmbedded, isAuthReady],
  );

  return (
    <PlaygroundAuthContext.Provider value={value}>
      {children}
    </PlaygroundAuthContext.Provider>
  );
}
